import { Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { StoreProductListItem } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';

import { displayTitle } from '../api/mappers';

import { ScheduleOwnerBadge } from './schedule-owner-badge';
import { ScheduledBadge } from './scheduled-badge';
import { StoreProductStatusBadge } from './store-product-status-badge';
import { StoreProductThumb } from './store-product-thumb';

/**
 * Column model of the store catalog table; `meta` ties columns to the list's sorts and filters.
 * The table adds the selection column (bulk publish) and the row menu itself.
 */
export function useStoreProductColumns(storeId: string) {
  const { t } = useTranslation('storeCatalog');
  const format = useFormat();

  return useMemo<Array<DataTableColumnDef<StoreProductListItem>>>(
    () => [
      {
        id: 'title',
        header: t('columns.product'),
        cell: ({ row }) => (
          <div className="flex min-w-0 items-center gap-3">
            <StoreProductThumb
              mediaId={row.original.featuredMediaId ?? row.original.product.featuredMediaId}
            />
            <div className="min-w-0">
              <Link
                to="/s/$storeId/catalog/$storeProductId"
                params={{ storeId, storeProductId: row.original.id }}
                className="block truncate font-medium hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {displayTitle(row.original)}
              </Link>
              <span className="text-muted-foreground block truncate text-xs">
                {row.original.title ? (
                  <>
                    {t('columns.masterTitle', { title: row.original.product.title })}
                    {' · '}
                  </>
                ) : null}
                <code>{row.original.handle}</code>
              </span>
            </div>
          </div>
        ),
        // The store override when set, else the master title: what this cell shows.
        meta: { label: t('columns.product'), sortField: 'title', lockVisibility: true },
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: t('columns.status'),
        cell: ({ row }) => (
          <div className="flex flex-wrap items-center gap-1">
            <StoreProductStatusBadge status={row.original.status} />
            <ScheduledBadge row={row.original} />
          </div>
        ),
        meta: { filterKey: 'status' },
        size: 180,
      },
      {
        id: 'schedule',
        header: t('columns.schedule'),
        cell: ({ row }) =>
          row.original.schedule ? (
            <ScheduleOwnerBadge schedule={row.original.schedule} withName />
          ) : null,
        meta: { label: t('columns.schedule'), filterKey: 'scheduleState' },
        size: 170,
      },
      {
        id: 'master',
        header: t('columns.master'),
        cell: ({ row }) => (
          <span className="text-muted-foreground text-xs">
            {t(`masterStatus.${row.original.product.status}`)}
            {' · '}
            {t('columns.variantCount', { count: row.original.product.variantCount })}
          </span>
        ),
        meta: { label: t('columns.master'), filterKey: 'productId' },
        size: 170,
      },
      {
        id: 'publishedAt',
        accessorKey: 'publishedAt',
        header: t('columns.publishedAt'),
        cell: ({ row }) =>
          row.original.publishedAt ? (
            <span className="text-muted-foreground" title={format.utc(row.original.publishedAt)}>
              {format.relative(row.original.publishedAt)}
            </span>
          ) : (
            <span className="text-muted-foreground">{t('columns.neverPublished')}</span>
          ),
        meta: { sortField: 'publishedAt', filterKey: 'publishedAt' },
        size: 150,
      },
    ],
    [format, storeId, t],
  );
}
